export type QualityTier = "low" | "med" | "high";

export interface QualitySettings {
  tier: QualityTier;
  pixelRatioCap: number;
  bloomEnabled: boolean;
  bloomResolutionScale: number;
  maxAnisotropy: number;
  shadowsEnabled: boolean;
  fxaaInsteadOfMsaa: boolean;
  maxActiveBodies: number;
  audioFftSize: number;
  textureMaxSize: number;
  targetFps: number;
}

const PRESETS: Record<QualityTier, Omit<QualitySettings, "tier">> = {
  low: {
    pixelRatioCap: 1.0,
    bloomEnabled: false,
    bloomResolutionScale: 0.5,
    maxAnisotropy: 0,
    shadowsEnabled: false,
    fxaaInsteadOfMsaa: false,
    maxActiveBodies: 2,
    audioFftSize: 256,
    textureMaxSize: 256,
    targetFps: 30,
  },
  med: {
    pixelRatioCap: 1.5,
    bloomEnabled: true,
    bloomResolutionScale: 0.5,
    maxAnisotropy: 2,
    shadowsEnabled: false,
    fxaaInsteadOfMsaa: true,
    maxActiveBodies: 3,
    audioFftSize: 256,
    textureMaxSize: 512,
    targetFps: 60,
  },
  high: {
    pixelRatioCap: 2.0,
    bloomEnabled: true,
    bloomResolutionScale: 1.0,
    maxAnisotropy: 4,
    shadowsEnabled: false,
    fxaaInsteadOfMsaa: true,
    maxActiveBodies: 4,
    audioFftSize: 512,
    textureMaxSize: 1024,
    targetFps: 60,
  },
};

export class QualityManager {
  settings: QualitySettings;

  constructor(initialTier: QualityTier = "high") {
    this.settings = { tier: initialTier, ...PRESETS[initialTier] };
  }

  detect(): QualitySettings {
    const tier = this.detectTier();
    this.settings = { tier, ...PRESETS[tier] };
    return this.settings;
  }

  setTier(tier: QualityTier): QualitySettings {
    this.settings = { tier, ...PRESETS[tier] };
    return this.settings;
  }

  demote(): QualitySettings | null {
    if (this.settings.tier === "high") return this.setTier("med");
    if (this.settings.tier === "med") return this.setTier("low");
    return null;
  }

  private detectTier(): QualityTier {
    if (typeof navigator === "undefined") return "high";
    const ua = navigator.userAgent || "";
    const isMobile = /Android|iPhone|iPad|iPod|Mobile|webOS/i.test(ua);
    const cores = (navigator as Navigator & { hardwareConcurrency?: number }).hardwareConcurrency ?? 4;
    const memory =
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;

    let score = 0;
    score += isMobile ? 0 : 2;
    score += cores >= 8 ? 2 : cores >= 4 ? 1 : 0;
    score += memory >= 8 ? 2 : memory >= 4 ? 1 : 0;

    const gpuRenderer = this.tryGetGpuRenderer();
    if (gpuRenderer) {
      const lower = gpuRenderer.toLowerCase();
      if (/(swiftshader|software|mali-4|mali-t|adreno 3|adreno 4)/.test(lower)) score -= 2;
      else if (/(rtx|radeon rx|m1|m2|m3|apple gpu)/.test(lower)) score += 2;
    }

    if (score >= 5) return "high";
    if (score >= 2) return "med";
    return "low";
  }

  private tryGetGpuRenderer(): string | null {
    try {
      const canvas = document.createElement("canvas");
      const gl =
        (canvas.getContext("webgl2") as WebGL2RenderingContext | null) ??
        (canvas.getContext("webgl") as WebGLRenderingContext | null);
      if (!gl) return null;
      const ext = gl.getExtension("WEBGL_debug_renderer_info");
      if (!ext) return null;
      return gl.getParameter((ext as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL) as string;
    } catch {
      return null;
    }
  }
}
